import { ReservationDocument, ReservationQueryVariables } from "@graphql/api";
import { processJWT } from "@lib/auth/jwt";
import Booking from "@lib/classes/Booking";
import { getContent } from "@lib/use_content";

function dropTypename(object) {
  const newObj = { ...object };
  if (newObj.__typename) {
    delete newObj.__typename;
  }
  return newObj;
}

export default async function readRes({
  resId,
  pid,
}: {
  resId?: number;
  pid?: string;
}): Promise<be.Booking | null> {
  const client = global.window.apolloClient;
  const queryVars: ReservationQueryVariables = { pid, reservationId: resId };
  const { data } = await client.query({
    fetchPolicy: "network-only",
    query: ReservationDocument,
    variables: queryVars,
  });

  const res = data?.reservation;
  if (!res) return null;

  if (res.auth?.token) await processJWT(res.auth.token);

  // Get service area name (for analytics). getContent REJECTS on a load failure
  // (Apollo's default errorPolicy "none") and this label is non-critical, so a
  // content-service blip must never abort loading the whole reservation.
  let serviceAreaName: string | undefined;
  if (res.serviceArea) {
    try {
      const { data: content } = await getContent(["serviceAreaName"], res.serviceArea);
      serviceAreaName = content?.content?.serviceAreaName ?? undefined;
    } catch (e) {
      console.error("Failed to load service-area name content:", e);
    }
  }

  const creditCards: be.TokenizedCreditCard[] = [];
  if (res.payment?.creditCard?.token?.length) {
    creditCards.push(res.payment.creditCard);
  }

  // Map userDefined fields to specialInfo
  const specialInfo: Record<string, any> = {};
  if (res.userDefined && Array.isArray(res.userDefined)) {
    res.userDefined.forEach((item: { field: string; value: any }) => {
      if (item.field && item.value !== undefined) {
        specialInfo[item.field] = item.value;
      }
    });
  }

  const booking = new Booking({
    contactInfo: res.contactInfo,
    creditCards,
    edit: true,
    itineraryId: res.itineraryId,
    legs: [
      {
        bagCount: res.bagCount,
        cancellable: res.cancellable,
        cancelled: res.cancelled,
        customerRequest: res.flight?.flightType == "R",
        dropoff: { ...dropTypename(res.dropoff), serviceArea: res.serviceArea },
        editable: res.editable,
        epicPassValidated: res.epicPassValidated,
        flags: res.flags,
        flight: {
          ...dropTypename(res.flight),
          flightType: res.flight?.flightType == "R" ? undefined : res.flight?.flightType, // We use leg.customerRequest flag to indicate cust request.
        },
        jobType: res.jobType,
        open: !!res.open,
        options: { ...dropTypename(res.options || {}) },
        passengers: res.passengers.map((p: be.FieldValue) => dropTypename(p)),
        pickup: { ...dropTypename(res.pickup), serviceArea: res.serviceArea },
        requestedPickupTime: res.service?.selectedTime?.pickupTime,
        resId: res.resId,
        resIdInternal: res.resIdInternal,
        returnTripStatus: res.returnTripStatus,
        service: dropTypename(res.service),
        serviceExtras: res.serviceExtras.map((se: be.FieldValue) => dropTypename(se)),
        sourceType: Number.isInteger(res.sourceType) ? res.sourceType : 60,
        stops: res.stops,
        userGratuityOverride: !!res.userGratuityOverride,
        userGratuityPercentage: res.userGratuityPercentage,
      },
    ],
    originalPayment: dropTypename(res.payment),
    otherInfo: { ...dropTypename(res.other), epicPassId: res.epicPassId },
    payment: dropTypename(res.payment),
    purchaseMethod: res.purchaseMethod,
    roundtrip: false,
    serviceArea: res.serviceArea,
    serviceAreaName: serviceAreaName,
    specialInfo,
    totalOriginal: res.totalOriginal,
  });

  if (booking.legs[0].service?.selectedTime)
    booking.legs[0].service.selectedTime = {
      ...booking.legs[0].service.selectedTime,
      customerSelected: true,
    };

  return booking;
}
